import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import SubjectAddress from '@Domain/Subjects/SubjectAddress';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import useSubjectOpener from '@UI/Pages/Beneficiaries/Shared/Hooks/useSubjectOpener';
import resolveFacilityPlacement from '@UI/Pages/Beneficiaries/BeneficiaryList/Helpers/resolveFacilityPlacement';
import SubjectCardFrame from '@UI/Pages/Beneficiaries/Shared/SubjectCard/SubjectCardFrame';
import SubjectCardIdentity from '@UI/Pages/Beneficiaries/Shared/SubjectCard/SubjectCardIdentity';
import SubjectCardMetadata from '@UI/Pages/Beneficiaries/Shared/SubjectCard/SubjectCardMetadata';
import SubjectCardTitle from '@UI/Pages/Beneficiaries/Shared/SubjectCard/SubjectCardTitle';
import buildSubjectInitials from '@UI/Pages/Subjects/Helpers/buildSubjectInitials';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import formatCardValue from '@UI/Pages/Subjects/SubjectList/SubjectCard/Helpers/formatCardValue';
import type SubjectFieldDefinitionView from '@UI/Store/SubjectTypes/Views/SubjectFieldDefinitionView';
import type SubjectView from '@UI/Store/Subjects/Views/SubjectView';
import * as React from 'react';
import { css } from '@emotion/react';
import { Tag } from '@verclam/ui-kit';

const SUBJECT_CARD_TRACK_COUNT = 1;
const SUBJECT_CARD_ROW_GAP = '0.625rem';
const FALLBACK_TITLE = 'Dossier';
const CARD_SUMMARY_FIELD_COUNT = 3;
const NO_FIELDS: readonly SubjectFieldDefinitionView[] = [];

const titleRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

type Props = {
	readonly subject: SubjectView;
	readonly typeKey: string;
	/** The active fields of the dossier type, in the order of its definition. */
	readonly fields?: readonly SubjectFieldDefinitionView[];
};

/**
 * One dossier of a configurable type, as a card: initials, title, and a line with its agency and its
 * creation date, then the values of its first sheet fields shown on the card. The whole card opens the dossier; a dossier of another agency switches agency first.
 */
const SubjectCard: React.FC<Props> = ({ subject, typeKey, fields = NO_FIELDS }) => {
	const { organizations } = useCurrentOrganization();
	const { currentFacility } = useStrictCurrentFacility();
	const openSubject = useSubjectOpener();
	const isInCurrentFacility = subject.facilityId === currentFacility.id;
	const path = buildSubjectPath(new SubjectAddress(typeKey, subject.id));
	const title = subject.title ?? FALLBACK_TITLE;

	const placement = React.useMemo(
		() => resolveFacilityPlacement(subject.facilityId, organizations),
		[subject.facilityId, organizations],
	);

	const metadataText = React.useMemo(() => {
		const createdOn = subject.createdAt.toLocaleDateString('fr-FR');

		return placement !== null ? `${placement.facilityName} · ${createdOn}` : createdOn;
	}, [placement, subject.createdAt]);

	const sheetSummary = React.useMemo(
		() =>
			fields
				.filter((field) => field.isShownOnCard)
				.map((field) => formatCardValue(field, subject.values.valueOf(field)))
				.filter((text) => text !== null)
				.slice(0, CARD_SUMMARY_FIELD_COUNT)
				.join(' · '),
		[fields, subject.values],
	);

	// A plain click on a dossier of another agency switches agency first; a modified click keeps the
	// native link behaviour (new tab, new window).
	const handleTitleLinkClick = React.useCallback(
		(event: React.MouseEvent<HTMLAnchorElement>) => {
			const hasModifierKey = event.ctrlKey || event.metaKey || event.shiftKey || event.altKey;

			if (isInCurrentFacility || hasModifierKey) {
				return;
			}

			event.preventDefault();
			openSubject(path, subject.facilityId);
		},
		[isInCurrentFacility, openSubject, path, subject.facilityId],
	);

	return (
		<SubjectCardFrame trackCount={SUBJECT_CARD_TRACK_COUNT} rowGap={SUBJECT_CARD_ROW_GAP}>
			<SubjectCardIdentity initials={buildSubjectInitials(subject.title)} seed={subject.id}>
				<div css={titleRowStyle}>
					<SubjectCardTitle to={path} onClick={handleTitleLinkClick}>
						{title}
					</SubjectCardTitle>
					<ConditionalLoader data={subject.isArchived}>
						<Tag value="Archivé" severity="secondary" rounded />
					</ConditionalLoader>
				</div>
				<SubjectCardMetadata>{metadataText}</SubjectCardMetadata>
				<ConditionalLoader data={sheetSummary.length > 0}>
					<SubjectCardMetadata>{sheetSummary}</SubjectCardMetadata>
				</ConditionalLoader>
			</SubjectCardIdentity>
		</SubjectCardFrame>
	);
};

export default SubjectCard;
